import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

export const Admin: React.FC = () => {
  const { user } = useAuth();
  const [canvasAuthorized, setCanvasAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState<any[]>([]);
  const [standards, setStandards] = useState<any[]>([]);
  const [selectedStandard, setSelectedStandard] = useState('');
  const [courseName, setCourseName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    checkCanvasAuth();
    loadData();
  }, []);

  const checkCanvasAuth = async () => {
    if (!user?.tenant_id) return;
    try {
      const response = await axios.get(`/api/oauth/canvas/status?tenantId=${user.tenant_id}`);
      setCanvasAuthorized(response.data.authorized);
    } catch (error) {
      console.error('Failed to check Canvas authorization:', error);
      setCanvasAuthorized(false);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    if (!user?.tenant_id) return;
    try {
      const [cohortsRes, standardsRes] = await Promise.all([
        axios.get(`/api/cohorts/tenant/${user.tenant_id}`),
        axios.get(`/api/standards/tenant/${user.tenant_id}`),
      ]);
      setCohorts(cohortsRes.data);
      setStandards(standardsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleAuthorize = () => {
    window.location.href = `/api/oauth/canvas/authorize?tenantId=${user?.tenant_id}`;
  };

  const handleRevoke = async () => {
    if (!user?.tenant_id) return;
    if (!window.confirm('Are you sure you want to revoke Canvas authorization? You will need to re-authorize to use Canvas features.')) return;

    try {
      setRevoking(true);
      await axios.get(`/api/oauth/canvas/revoke?tenantId=${user.tenant_id}`);
      setCanvasAuthorized(false);
      alert('Canvas authorization revoked successfully. Please re-authorize with the updated permissions.');
    } catch (error) {
      console.error('Failed to revoke authorization:', error);
      alert('Failed to revoke Canvas authorization');
    } finally {
      setRevoking(false);
    }
  };

  const handleCreateCourse = async () => {
    if (!user?.tenant_id || !selectedStandard || !courseName) return;

    try {
      setCreating(true);
      await axios.post('/api/canvas-api/create-course', {
        tenant_id: user.tenant_id,
        standard_id: selectedStandard,
        course_name: courseName,
      });
      alert('Course created successfully!');
      loadData();
      setSelectedStandard('');
      setCourseName('');
    } catch (error: any) {
      console.error('Failed to create course:', error);
      alert(error.response?.data?.message || 'Failed to create course');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
        <p className="text-gray-600">Manage Canvas integration and create apprenticeship courses</p>
      </div>

      {/* Canvas Authorization */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Canvas API Authorization</h2>
        
        {canvasAuthorized ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              <div>
                <p className="text-gray-900 font-medium">Canvas API is Authorized</p>
                <p className="text-sm text-gray-600">The application can create and manage Canvas courses</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={checkCanvasAuth}
                className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition"
              >
                Refresh Status
              </button>
              <button
                onClick={handleRevoke}
                disabled={revoking}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition disabled:bg-gray-400"
              >
                {revoking ? 'Revoking...' : 'Revoke Authorization'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
              <div>
                <p className="text-gray-900 font-medium">Canvas API Not Authorized</p>
                <p className="text-sm text-gray-600">Click authorize to connect with Canvas</p>
              </div>
            </div>
            <button
              onClick={handleAuthorize}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Authorize Canvas API
            </button>
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> If you've recently updated the system, you may need to revoke and re-authorize 
            to grant new permissions (such as sending messages to users).
          </p>
        </div>
      </div>

      {/* Create Canvas Course */}
      {canvasAuthorized && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Canvas Course</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Apprenticeship Standard
              </label>
              <select
                value={selectedStandard}
                onChange={(e) => setSelectedStandard(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select Standard --</option>
                {standards.map((standard) => (
                  <option key={standard.id} value={standard.id}>
                    {standard.reference} - {standard.title} (Level {standard.level})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Course Name
              </label>
              <input
                type="text"
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                placeholder="e.g., Software Developer Cohort 2024"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleCreateCourse}
              disabled={creating || !selectedStandard || !courseName}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition disabled:bg-gray-400"
            >
              {creating ? 'Creating Course...' : 'Create Course in Canvas'}
            </button>
          </div>

          {cohorts.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Existing Courses</h3>
              <div className="space-y-2">
                {cohorts.map((cohort) => (
                  <div key={cohort.id} className="p-3 bg-gray-50 rounded-md">
                    <p className="font-medium text-gray-900">{cohort.name}</p>
                    <p className="text-sm text-gray-600">Canvas Course ID: {cohort.canvasCourseId}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
